// Table.jsx
// Client: vmfive
// Version: 1.0.0
// Product: Ad SaaS — Data table with sortable columns, row click, and empty state

import React from 'react';
import './Table.css';

/**
 * Column definition:
 * {
 *   key: string,          — data field key
 *   title: string,        — column header text
 *   width: string,        — optional CSS width (e.g. '120px', '20%')
 *   align: string,        — 'left' | 'center' | 'right' (default: 'left')
 *   sortable: boolean,    — show sort indicator
 *   render: (value, row) => ReactNode  — custom cell renderer
 * }
 */

export default function Table({
  columns = [],
  data = [],
  rowKey = 'id',
  onRowClick,
  sortField,
  sortOrder,
  onSort,
  emptyText = 'No data',
  className = '',
  loading = false,
}) {
  const handleHeaderClick = (col) => {
    if (!col.sortable || !onSort) return;
    const nextOrder =
      sortField === col.key && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(col.key, nextOrder);
  };

  return (
    <div className={`table-wrapper ${className}`}>
      <table className="table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={[
                  'table__th',
                  col.align ? `table__th--${col.align}` : '',
                  col.sortable ? 'table__th--sortable' : '',
                  sortField === col.key ? 'table__th--sorted' : '',
                ].filter(Boolean).join(' ')}
                style={col.width ? { width: col.width } : undefined}
                onClick={() => handleHeaderClick(col)}
              >
                <span className="table__th-content">
                  {col.title}
                  {col.sortable && (
                    <span className="table__sort-icon">
                      {sortField === col.key ? (
                        sortOrder === 'asc' ? '↑' : '↓'
                      ) : (
                        '↕'
                      )}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={columns.length} className="table__empty">
                Loading...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="table__empty">
                {emptyText}
              </td>
            </tr>
          ) : (
            data.map((row) => {
              const key = typeof rowKey === 'function' ? rowKey(row) : row[rowKey];
              return (
                <tr
                  key={key}
                  className={[
                    'table__row',
                    onRowClick ? 'table__row--clickable' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={col.align ? `table__td--${col.align}` : ''}
                    >
                      {col.render
                        ? col.render(row[col.key], row)
                        : row[col.key] ?? '—'}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
